/**
 * LicensorConsoleClientDetail — ClientDetail panel for LicensorConsole.
 * Extracted from LicensorConsole.jsx to reduce file complexity.
 */
import React, { useState, useEffect } from 'react';
import { ChevronRight, TrendingUp, TrendingDown, Minus, Clock, CheckCircle, XCircle, Database, User, KeyRound, RefreshCw } from 'lucide-react';
import { Section, SectionHeader, Mini, KeyValueGrid, DataTable, Skeleton, ErrorBanner } from './LicensorConsolePrimitives';
import { API, apiFetch } from '../lib/api';

// ── Tier rank for upgrade/downgrade detection ─────────────────────────────────
const TIER_RANK = {
  'lite': 0, 'retailcliq lite': 0,
  'express': 1, 'express retail': 1,
  'standard': 2,
  'professional': 3, 'pro': 3,
  'enterprise': 4, 'enterprise unlimited': 4,
  'hybrid model': 2,
};
const TIER_COLOR = {
  'lite': '#10b981',        'retailcliq lite': '#10b981',
  'express': '#06b6d4',     'express retail': '#06b6d4',
  'standard': '#3b82f6',
  'professional': '#6366f1','pro': '#6366f1',
  'enterprise': '#8b5cf6',  'enterprise unlimited': '#8b5cf6',
  'hybrid model': '#f59e0b',
};
function tierColor(tier) { return TIER_COLOR[(tier || '').toLowerCase()] || '#64748b'; }
function tierRank(tier)  { return TIER_RANK[(tier || '').toLowerCase()] ?? -1; }

function fmt(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return String(d).slice(0, 10); }
}
// Returns null for epoch, pre-2020, or invalid dates (suppresses bad legacy dates)
function validTs(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (isNaN(d.getTime()) || d.getFullYear() < 2020) return null;
  return ts;
}

// ── ActivationTimeline ────────────────────────────────────────────────────────
function ActivationTimeline({ master = {}, licenses = [], signupMetadata = [] }) {
  const activeLicense = licenses.find(l => l.Status === 'Active');
  const firstMeta     = (signupMetadata || []).sort((a, b) => new Date(a.CreatedDate) - new Date(b.CreatedDate))[0];

  const steps = [
    {
      key:   'signup',
      label: 'Signup Initiated',
      ts:    validTs(firstMeta?.CreatedDate) || validTs(master.CreatedDate),
      done:  !!master.ClientId,
      color: '#3b82f6',
      detail: firstMeta ? `IP: ${firstMeta.RequestIp || '—'} · Platform: ${firstMeta.Platform || '—'}` : (validTs(master.CreatedDate) ? `Created: ${fmt(master.CreatedDate)}` : 'Pre-provisioned fixture')
    },
    {
      key:   'email',
      label: 'Email Verified',
      ts:    validTs(master.EmailIdConfirmed),
      done:  !!master.EmailIdConfirmed,
      color: '#10b981',
      detail: master.EmailId || '—'
    },
    {
      key:   'payment',
      label: 'Payment',
      ts:    validTs(master.paymentDate) || (master.paymentStatus === 'PAID' ? validTs(activeLicense?.IssuedDate) : null),
      done:  master.paymentStatus === 'PAID' || master.tier_price === 0 || master.tier_price === '0',
      skipped: master.paymentStatus === 'FREE' || master.tier_price === 0,
      color: '#a78bfa',
      detail: master.paymentStatus === 'FREE' ? 'Free tier — no payment' : (master.paymentStatus || (master.tier_price === 0 ? 'Free' : 'Pending'))
    },
    {
      key:   'provision',
      label: 'Tenant Provisioned',
      ts:    validTs(activeLicense?.IssuedDate),
      done:  !!activeLicense,
      color: '#f59e0b',
      detail: activeLicense ? `License: ${(activeLicense.LicenseKey || '').slice(0, 16)}…  Tier: ${activeLicense.LicenseTier}` : 'License not yet issued'
    },
    {
      key:   'db',
      label: 'Database Ready',
      ts:    validTs(activeLicense?.IssuedDate),
      done:  !!master.SQLDBName,
      color: '#06b6d4',
      detail: master.SQLDBName ? `${master.SQLDBName}${master.DataBaseCreated ? ' (dedicated)' : ' (pool)'}` : '—'
    },
    {
      key:   'welcome',
      label: 'Welcome Email',
      ts:    validTs(activeLicense?.IssuedDate),
      done:  !!activeLicense && !!master.EmailId,
      color: '#10b981',
      detail: master.EmailId ? `Sent to ${master.EmailId}` : 'No email on record'
    },
  ];

  return (
    <div style={{ padding: '16px 18px', background: 'rgba(15,23,42,0.6)', borderRadius: 10, border: '1px solid rgba(148,163,184,0.12)' }} data-testid="activation-timeline">
      <div style={{ color: '#64748b', fontSize: 11, fontWeight: 700, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Clock size={12} color="#3b82f6" /> PROVISIONING TIMELINE
      </div>
      <div style={{ display: 'flex', gap: 0, overflowX: 'auto' }}>
        {steps.map((step, idx) => (
          <div key={step.key} style={{ display: 'flex', alignItems: 'flex-start', flex: '1 1 0' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0 }}>
              {/* connector line */}
              <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginBottom: 8 }}>
                {idx > 0 && <div style={{ flex: 1, height: 2, background: steps[idx - 1].done ? '#10b981' : 'rgba(148,163,184,0.2)' }} />}
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: step.done ? 'rgba(16,185,129,0.15)' : step.skipped ? 'rgba(100,116,139,0.15)' : 'rgba(148,163,184,0.1)',
                  border: `2px solid ${step.done ? '#10b981' : step.skipped ? '#475569' : 'rgba(148,163,184,0.3)'}`,
                }}>
                  {step.done ? <CheckCircle size={13} color="#10b981" /> : <XCircle size={13} color={step.skipped ? '#475569' : 'rgba(148,163,184,0.5)'} />}
                </div>
                {idx < steps.length - 1 && <div style={{ flex: 1, height: 2, background: step.done ? '#10b981' : 'rgba(148,163,184,0.2)' }} />}
              </div>
              {/* label */}
              <div style={{ textAlign: 'center', paddingLeft: 4, paddingRight: 4 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: step.done ? '#f1f5f9' : '#64748b', marginBottom: 2 }}>{step.label}</div>
                <div style={{ fontSize: 9, color: '#64748b' }}>{step.ts ? fmt(step.ts) : step.done ? '✓' : '—'}</div>
                <div style={{ fontSize: 9, color: '#475569', marginTop: 2, lineHeight: 1.4 }}>{step.detail}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── LicenseTimeline ───────────────────────────────────────────────────────────
function LicenseTimeline({ licenses = [] }) {
  if (!licenses.length) return (
    <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>No license records found.</div>
  );

  // Sort oldest → newest so the timeline reads left→right / top→bottom
  const sorted = [...licenses].sort((a, b) => new Date(a.IssuedDate) - new Date(b.IssuedDate));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {sorted.map((lic, idx) => {
        const isActive  = lic.Status === 'Active';
        const isExpired = !isActive && new Date(lic.ExpiryDate) < new Date();
        const color     = tierColor(lic.LicenseTier);
        const daysLeft  = Math.ceil((new Date(lic.ExpiryDate) - new Date()) / 86400000);

        // Transition indicator from previous entry
        let transition = null;
        if (idx > 0) {
          const prevRank = tierRank(sorted[idx - 1].LicenseTier);
          const curRank  = tierRank(lic.LicenseTier);
          if (curRank > prevRank)      transition = { icon: <TrendingUp  size={12} />, label: 'Upgrade',   color: '#16a34a', bg: '#f0fdf4' };
          else if (curRank < prevRank) transition = { icon: <TrendingDown size={12} />, label: 'Downgrade', color: '#dc2626', bg: '#fef2f2' };
          else                         transition = { icon: <Minus        size={12} />, label: 'Renewed',   color: '#64748b', bg: '#f8fafc' };
        }

        return (
          <div key={lic.LicenseId || idx}>
            {/* Transition connector */}
            {transition && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 16px' }}>
                <div style={{ width: 2, height: 14, background: '#e2e8f0', marginLeft: 14 }} />
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px',
                  borderRadius: 12, background: transition.bg, color: transition.color,
                  marginLeft: 4,
                }}>
                  {transition.icon} {transition.label}
                </span>
              </div>
            )}

            {/* License card */}
            <div
              data-testid={`license-timeline-entry-${idx}`}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                padding: '8px 12px',
                background: isActive ? `${color}08` : '#fff',
                border: `1px solid ${isActive ? color + '44' : '#f1f5f9'}`,
                borderRadius: 8,
                margin: '2px 0',
                position: 'relative',
                borderLeft: `4px solid ${color}`,
              }}
            >
              {/* Timeline dot */}
              <div style={{
                width: 10, height: 10, borderRadius: '50%',
                background: isActive ? color : '#cbd5e1',
                flexShrink: 0, marginTop: 4,
                boxShadow: isActive ? `0 0 0 3px ${color}33` : 'none',
              }} />

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                  {/* Tier badge */}
                  <span style={{
                    fontSize: '0.75rem', fontWeight: 800, padding: '2px 8px',
                    borderRadius: 20, background: color, color: '#fff',
                    letterSpacing: '0.02em',
                  }}>
                    {lic.LicenseTier || '—'}
                  </span>

                  {/* Status pill */}
                  {isActive ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.72rem', fontWeight: 700, color: '#16a34a', background: '#f0fdf4', padding: '2px 7px', borderRadius: 12 }}>
                      <CheckCircle size={10} /> Current
                    </span>
                  ) : isExpired ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.72rem', fontWeight: 700, color: '#dc2626', background: '#fef2f2', padding: '2px 7px', borderRadius: 12 }}>
                      <XCircle size={10} /> Expired
                    </span>
                  ) : (
                    <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', background: '#f1f5f9', padding: '2px 7px', borderRadius: 12 }}>
                      Inactive
                    </span>
                  )}

                  {/* Days left for active */}
                  {isActive && daysLeft > 0 && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.7rem', color: daysLeft < 30 ? '#f59e0b' : '#94a3b8' }}>
                      <Clock size={10} /> {daysLeft}d left
                    </span>
                  )}
                </div>

                {/* Key facts row */}
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '0.75rem', color: '#64748b' }}>
                  <span><strong style={{ color: '#475569' }}>Issued:</strong> {fmt(lic.IssuedDate)}</span>
                  <span><strong style={{ color: '#475569' }}>Expires:</strong> {fmt(lic.ExpiryDate)}</span>
                  {lic.NumberOfUsers  > 0 && <span><strong style={{ color: '#475569' }}>Users:</strong> {lic.NumberOfUsers}</span>}
                  {lic.NumberOfSystems > 0 && <span><strong style={{ color: '#475569' }}>Systems:</strong> {lic.NumberOfSystems}</span>}
                  {lic.Amount > 0 && <span><strong style={{ color: '#475569' }}>₹</strong> {Number(lic.Amount).toLocaleString()}</span>}
                </div>

                {/* License Key — truncated mono */}
                <div style={{ marginTop: 3, fontSize: '0.7rem', fontFamily: 'monospace', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {lic.LicenseKey || '—'}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── ResetPasswordCell ─────────────────────────────────────────────────────────
function ResetPasswordCell({ clientId, compId, userId }) {
  const [open,     setOpen]     = useState(false);
  const [pass,     setPass]     = useState('');
  const [busy,     setBusy]     = useState(false);
  const [result,   setResult]   = useState(null); // { ok, tempPassword, error }

  function reset() { setOpen(false); setPass(''); setResult(null); }

  async function submit() {
    setBusy(true);
    try {
      const r = await apiFetch(`${API}/licensor/reset-erp-user-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, compId, userId, newPassword: pass.trim() || undefined })
      });
      const d = await r.json();
      setResult(d);
    } catch (e) {
      setResult({ ok: false, error: e.message });
    }
    setBusy(false);
  }

  if (result) {
    return (
      <div data-testid={`reset-result-${compId}-${userId}`}
        style={{ padding: '6px 0' }}>
        {result.ok
          ? <>
              <div style={{ fontSize: 10, color: '#10b981', fontWeight: 700 }}>✓ Password reset</div>
              <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#f1f5f9', background: 'rgba(16,185,129,0.1)', padding: '3px 7px', borderRadius: 4, marginTop: 3, display: 'inline-block', letterSpacing: '0.05em' }}
                data-testid={`new-password-${compId}-${userId}`}>
                {result.tempPassword}
              </div>
              {result.saasUpdated && (
                <div style={{ fontSize: 9, color: '#06b6d4', marginTop: 2 }}>SaaS layer synced ({result.saasUserType})</div>
              )}
              <button onClick={reset} style={{ marginLeft: 6, fontSize: 9, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Dismiss</button>
            </>
          : <>
              <div style={{ fontSize: 10, color: '#ef4444' }}>{result.error || 'Reset failed'}</div>
              <button onClick={reset} style={{ fontSize: 9, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', marginTop: 2 }}>Retry</button>
            </>
        }
      </div>
    );
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid={`reset-erp-user-${compId}-${userId}`}
        title={`Reset ERP password for ${userId}`}
        style={{ background: 'none', border: '1px solid rgba(148,163,184,0.25)', borderRadius: 5, padding: '3px 9px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap' }}>
        <KeyRound size={9} /> Reset
      </button>
    );
  }

  return (
    <div data-testid={`reset-form-${compId}-${userId}`}
      style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
      <input
        type="text"
        placeholder="Leave blank to auto-generate"
        value={pass}
        onChange={e => setPass(e.target.value)}
        style={{ fontSize: 10, padding: '3px 7px', borderRadius: 4, border: '1px solid rgba(148,163,184,0.4)', background: 'rgba(15,23,42,0.6)', color: '#f1f5f9', width: 170, outline: 'none' }}
        data-testid={`reset-password-input-${compId}-${userId}`}
        onKeyDown={e => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') reset(); }}
        autoFocus
      />
      <button onClick={submit} disabled={busy}
        data-testid={`reset-confirm-${compId}-${userId}`}
        style={{ fontSize: 10, padding: '3px 10px', borderRadius: 4, background: '#ef4444', border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 700 }}>
        {busy ? '…' : 'Confirm'}
      </button>
      <button onClick={reset} style={{ fontSize: 10, padding: '3px 7px', borderRadius: 4, background: 'none', border: '1px solid rgba(148,163,184,0.25)', color: '#94a3b8', cursor: 'pointer' }}>Cancel</button>
    </div>
  );
}

// ── ErpUsersSection ───────────────────────────────────────────────────────────
function ErpUsersSection({ clientId, dbName }) {
  const [users,   setUsers]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [dbMeta,  setDbMeta]  = useState(null);

  const load = () => {
    setLoading(true); setError(null);
    apiFetch(`${API}/licensor/tenant-db-users/${encodeURIComponent(clientId)}`)
      .then(r => r.json())
      .then(d => {
        if (d.error && !d.users) { setError(d.error); }
        else { setUsers(d.users || []); setDbMeta({ dbName: d.dbName, dbType: d.dbType, note: d.note, error: d.error }); }
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  useEffect(() => { if (dbName) load(); }, [clientId, dbName]); // eslint-disable-line

  const pwdColor = (v) => v === 'SET' ? '#10b981' : '#ef4444';

  return (
    <Section
      title="ERP UserMaster"
      sub={dbMeta ? `${dbMeta.dbName} · ${dbMeta.dbType || '—'}${dbMeta.error ? ' · ' + dbMeta.error : ''}` : (dbName || 'No DB assigned')}
    >
      {!dbName && (
        <div style={{ padding: '16px', textAlign: 'center', color: '#64748b', fontSize: 12 }}>No tenant database assigned yet.</div>
      )}
      {dbName && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <button onClick={load} disabled={loading}
            data-testid="erp-users-refresh"
            style={{ background: 'none', border: '1px solid rgba(148,163,184,0.3)', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#94a3b8' }}>
            <RefreshCw size={10} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /> {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      )}
      {error && !loading && (
        <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.08)', borderRadius: 6, color: '#f87171', fontSize: 11 }}>{error}</div>
      )}
      {users && !loading && (
        <>
          {users.length === 0 ? (
            <div style={{ padding: '16px', textAlign: 'center', color: '#64748b', fontSize: 12 }}>No UserMaster rows found in {dbName}.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ background: 'rgba(148,163,184,0.06)' }}>
                    {['CompId', 'UserId', 'UserName', 'Status', 'Password', 'Reset'].map(h => (
                      <th key={h} style={{ padding: '7px 10px', color: '#94a3b8', fontWeight: 700, textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid rgba(148,163,184,0.15)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(148,163,184,0.08)' }} data-testid={`erp-user-row-${i}`}>
                      <td style={{ padding: '6px 10px', fontFamily: 'monospace', color: '#06b6d4', fontSize: 10 }}>{u.CompId || '—'}</td>
                      <td style={{ padding: '6px 10px', fontFamily: 'monospace', color: '#f1f5f9', fontWeight: 600 }}>{u.UserId || '—'}</td>
                      <td style={{ padding: '6px 10px', color: '#94a3b8' }}>{u.UserName || '—'}</td>
                      <td style={{ padding: '6px 10px' }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: u.Status === 'S' ? '#10b981' : '#64748b' }}>{u.Status || '—'}</span>
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: pwdColor(u.PwdSet) }}>{u.PwdSet || '—'}</span>
                      </td>
                      <td style={{ padding: '4px 10px' }}>
                        <ResetPasswordCell clientId={clientId} compId={u.CompId} userId={u.UserId} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {dbMeta?.note && (
                <div style={{ padding: '6px 10px', fontSize: 10, color: '#64748b', borderTop: '1px solid rgba(148,163,184,0.1)', marginTop: 2 }}>{dbMeta.note}</div>
              )}
            </div>
          )}
        </>
      )}
    </Section>
  );
}

export function ClientDetail({ clientId, onBack }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    apiFetch(`${API}/licensor/client/${encodeURIComponent(clientId)}`)
      .then(r => r.json())
      .then(d => { if (d.error) setError(d.error); else setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [clientId]);

  if (!clientId) return <ErrorBanner error="No client selected" />;
  if (loading)   return <Skeleton label={`Loading 360° view for ${clientId}...`} />;
  if (error)     return <ErrorBanner error={error} />;

  const m = data?.master || {};
  const licenses = data?.licenses || [];
  const activeCount   = licenses.filter(l => l.Status === 'Active').length;
  const inactiveCount = licenses.filter(l => l.Status !== 'Active').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <SectionHeader title={`Client 360°: ${m.ClientName || m.CompName || clientId}`} badge={clientId} />
        <button className="btn btn-outline" onClick={onBack} data-testid="licensor-client-back">
          <ChevronRight size={12} style={{ transform: 'rotate(180deg)' }} /> Back to list
        </button>
      </div>

      <div className="lc-stat-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        <Mini label="Status"     value={m.Status || '—'} />
        <Mini label="Tier"       value={m.tier_code || '—'} />
        <Mini label="Payment"    value={m.paymentStatus || '—'} sub={m.paymentAmount ? `₹ ${m.paymentAmount}` : ''} />
        <Mini label="license_id" value={m.license_id || '—'} mono />
      </div>

      <ActivationTimeline master={m} licenses={licenses} signupMetadata={data?.signupMetadata} />

      <Section title="WebApp.ClientMaster" sub="Root customer record">
        <KeyValueGrid obj={m} skip={['ClientPassword','AltPassword','SQLPassword','EmailOTP','PhOTP1','PhOTP2']} />
      </Section>

      <Section title="WebApp.ClientMaster_Child" sub={`${data?.childRecords?.length || 0} login users`}>
        <DataTable rows={data?.childRecords} columns={['ClientId','UserType','ClientName','EmailId','IpAddress','GlobalAddress','Status','MClientId']} />
      </Section>

      <Section title="ERP DB Connection" sub={`Tenant database: ${m.SQLDBName || '—'} · Schema: ${m.SQLSchemaName || '—'} · ${m.DataBaseCreated ? 'Dedicated live DB ✓' : 'Pool / template DB'}`}>
        <div className="lc-stat-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <Mini label="Server"    value={m.SQLServerId   || '—'} mono />
          <Mini label="Database"  value={m.SQLDBName     || '—'} mono />
          <Mini label="Schema"    value={m.SQLSchemaName || '—'} mono />
          <Mini label="DB User"   value={m.SQLUserName   || '—'} mono />
          <Mini label="Live DB"   value={m.DataBaseCreated ? 'Yes (Dedicated)' : 'No (Pool)'} />
          <Mini label="ERP CompId" value={`RC${m.ClientId?.split('_')[2] || '?'}`} mono />
        </div>
      </Section>

      <ErpUsersSection clientId={clientId} dbName={m.SQLDBName} />

      {/* ── License History Timeline ─────────────────────────────────────── */}
      <Section
        title={`License History (${licenses.length} total)`}
        sub={`${activeCount} active · ${inactiveCount} superseded · ordered oldest → newest`}
      >
        <LicenseTimeline licenses={licenses} />
      </Section>

      <Section title="WebApp.LicenseRegistry" sub="Signed registry entries (raw audit)">
        <DataTable rows={data?.registry} columns={['LicenseId','IssuedAt','ExpiresAt','Packages','MaxUsers','MaxDevices','IssuedBy','Status']} />
      </Section>

      <Section title="WebApp.ClientSignup_Metadata" sub="Signup telemetry">
        <DataTable rows={data?.signupMetadata} columns={['LocationStatus','Platform','UserAgent','BiometricLivenessCompleted','RequestIp','CreatedDate']} />
      </Section>
    </div>
  );
}
